import React, { Component, RefObject, createRef } from 'react'
import Base from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Icon from './Icon'
import './DatePicker/DatePicker.css'


interface DateRangePickerProps {
  startAt: string
  endAt: string
  onChange: (startAt: string, endAt: string) => void
}

interface DateRangePickerState {
  open: boolean
  activePreset: string
}

interface Preset {
  name: string
  range: () => [Date, Date]
}

export default class DateRangePicker extends Component<DateRangePickerProps, DateRangePickerState> {
  wrapperRef: RefObject<HTMLDivElement>

  constructor (props: DateRangePickerProps) {
    super(props)
    this.state = {
      open: false,
      activePreset: ''
    }
    this.wrapperRef = createRef()
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
  }

  get startDate () {
    return this.props.startAt ? new Date(this.props.startAt) : null
  }

  get endDate () {
    return this.props.endAt ? new Date(this.props.endAt) : null
  }

  get label () {
    const { startDate, endDate } = this
    if (!startDate || !endDate) return 'Date range'
    return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`
  }

  get presets (): Preset[] {
    return [
      { name: 'Today', range: () => this.dayRange(new Date()) },
      { name: 'Yesterday', range: () => this.dayRange(this.addDays(new Date(), -1)) },
      { name: 'Last 7 Days', range: () => [this.startOfDay(this.addDays(new Date(), -6)), this.endOfDay(new Date())] },
      { name: 'Current Month', range: () => [this.startOfMonth(new Date()), this.endOfMonth(new Date())] },
      { name: 'Last 30 Days', range: () => [this.startOfDay(this.addDays(new Date(), -29)), this.endOfDay(new Date())] },
      { name: 'Last 3 Months', range: () => [this.startOfDay(this.addMonths(new Date(), -3)), this.endOfDay(new Date())] },
      { name: 'Last 12 Months', range: () => [this.startOfDay(this.addMonths(new Date(), -12)), this.endOfDay(new Date())] }
    ]
  }

  get monthPresets (): Preset[] {
    const now = new Date()
    return Array.from({ length: 12 }, (_, month) => {
      const year = month <= now.getMonth()
        ? now.getFullYear()
        : now.getFullYear() - 1
      const date = new Date(year, month, 1)
      const monthName = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date)
      const shortYear = String(year).slice(-2)

      return {
        name: `${monthName} '${shortYear}`,
        range: () => [this.startOfMonth(date), this.endOfMonth(date)] as [Date, Date]
      }
    })
  }

  formatDate (date: Date) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date)
  }

  startOfDay (date: Date) {
    const next = new Date(date)
    next.setHours(0, 0, 0, 0)
    return next
  }

  endOfDay (date: Date) {
    const next = new Date(date)
    next.setHours(23, 59, 59, 999)
    return next
  }

  startOfMonth (date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0)
  }

  endOfMonth (date: Date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)
  }

  addDays (date: Date, days: number) {
    const next = new Date(date)
    next.setDate(next.getDate() + days)
    return next
  }

  addMonths (date: Date, months: number) {
    const next = new Date(date)
    next.setMonth(next.getMonth() + months)
    return next
  }

  dayRange (date: Date): [Date, Date] {
    return [this.startOfDay(date), this.endOfDay(date)]
  }

  handleToggle = () => {
    this.setState(state => ({ open: !state.open }))
  }

  handlePresetClick = (preset: Preset) => {
    const [startAt, endAt] = preset.range()
    this.props.onChange(startAt.toISOString(), endAt.toISOString())
    this.setState({
      activePreset: preset.name,
      open: false
    })
  }

  handleDateChange = (dates: any) => {
    const [startAt, endAt] = dates
    this.props.onChange(
      startAt ? this.startOfDay(startAt).toISOString() : '',
      endAt ? this.endOfDay(endAt).toISOString() : ''
    )
    this.setState({
      activePreset: '',
      open: Boolean(startAt && !endAt)
    })
  }

  handleDocumentClick (event: MouseEvent) {
    const target = event.target as Node
    const wrapper = this.wrapperRef.current
    if (wrapper && !wrapper.contains(target)) {
      this.setState({ open: false })
    }
  }

  componentDidMount () {
    document.addEventListener('mousedown', this.handleDocumentClick)
  }

  componentDidUpdate (prevProps: DateRangePickerProps) {
    const cleared = (prevProps.startAt || prevProps.endAt) && !this.props.startAt && !this.props.endAt
    if (cleared && this.state.activePreset) {
      this.setState({ activePreset: '' })
    }
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleDocumentClick)
  }

  render () {
    const calendar = React.createElement(Base as any, {
      inline: true,
      selectsRange: true,
      startDate: this.startDate,
      endDate: this.endDate,
      onChange: this.handleDateChange,
      monthsShown: 1,
      calendarClassName: 'datePicker'
    })

    return (
      <div className="relative mb-4 min-w-0" ref={this.wrapperRef}>
        <button
          type="button"
          className="input-default !w-auto max-w-full mb-0 text-left inline-flex items-center gap-2 min-w-[260px]"
          onClick={this.handleToggle}>
          <Icon name="calendar" size="small" color="gray" />
          <span className="truncate min-w-0">{this.label}</span>
        </button>
        {this.state.open && (
          <div className="absolute left-0 xl:left-auto xl:right-0 z-[60] mt-1 bg-white border border-primary shadow-lg flex flex-col lg:flex-row max-w-[calc(100vw-2rem)]">
            <div className="p-3 overflow-x-auto">
              {calendar}
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-primary flex w-full lg:w-auto lg:max-h-[264px] shrink-0">
              <div className="py-2 w-40">
                {this.presets.map(preset => (
                  <button
                    type="button"
                    key={preset.name}
                    className={`block w-full text-left px-4 py-2 hover:bg-tertiary ${this.state.activePreset === preset.name ? 'bg-tertiary' : 'bg-white'}`}
                    onClick={() => this.handlePresetClick(preset)}>
                    {preset.name}
                  </button>
                ))}
              </div>
              <div className="border-l border-primary py-2 w-28 overflow-y-auto">
                {this.monthPresets.map(preset => (
                  <button
                    type="button"
                    key={preset.name}
                    className={`block w-full text-left px-4 py-2 whitespace-nowrap hover:bg-tertiary ${this.state.activePreset === preset.name ? 'bg-tertiary' : 'bg-white'}`}
                    onClick={() => this.handlePresetClick(preset)}>
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
}
